import React, { useContext, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Col, Row, Table } from "antd";

import defaultLogo from "../../assets/images/default_company_logo.jpg";
import {
  getRecomendationCompanyData,
  getRecomendationEmployeeData,
} from "../../actionCreator/dashboardActionCreator";
import { getUserInfo, getPartialPhoneNumber } from "../../utils/utils";
import { getCompanyLogoUrl } from "../../utils";
import { AuthContext } from "../../context/AuthContext";
import CustomPagination from "../../common/CustomPagination";
import { useAlert } from "../../context/AlertProvider";

const RecommendedReport = ({ userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isExecutiveView, isCompanyView } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const requestRef = useRef(0);

  const isMyDashboard = user?.id == userId;
  const [companyList, setCompanyList] = useState();
  const [employeeList, setEmployeeList] = useState();
  const recommendedEmployeeList = useSelector(
    (state) => state.DashboardReducer.recommendedEmployeeData,
  );
  const recommendedCompanyList = useSelector(
    (state) => state.DashboardReducer.recommendedCompanyData,
  );

  useEffect(() => {
    if (Object.keys(getUserInfo()).length) {
      const payload = { userId };
      const requestId = ++requestRef.current;
      setIsLoading(true);
      Promise.all([
        dispatch(getRecomendationCompanyData(payload)),
        dispatch(getRecomendationEmployeeData(payload)),
      ]).finally(() => {
        if (requestId === requestRef.current) {
          setIsLoading(false);
        }
      });
    }
  }, [dispatch, userId]);

  useEffect(() => {
    let data = [];
    if (!recommendedCompanyList) return;
    recommendedCompanyList?.forEach((record) => {
      data = [
        ...data,
        {
          key: record.id,
          name: record,
          industry: record?.industry?.name,
          phone: record.phoneNo,
        },
      ];
    });
    setCompanyList(data);
  }, [recommendedCompanyList]);

  useEffect(() => {
    let data = [];
    if (!recommendedEmployeeList) return;
    recommendedEmployeeList?.forEach((record) => {
      data = [
        ...data,
        {
          key: record.id,
          id: record.id,
          name: record,
          company: record?.company?.name,
          email: record?.emailId,
          phone: record?.company?.phoneNo,
          mobile: record.phoneNo,
          designation: record?.title,
        },
      ];
    });
    setEmployeeList(data);
  }, [recommendedEmployeeList]);

  const getDetails = (id, name) => {
    if (!isMyDashboard) {
      showAlert(
        "info",
        "Redirection is not allowed when viewing another user's dashboard.",
      );
      return;
    }
    let cname = name.replaceAll(" ", "-", name);
    navigate(`/company-summary/${id}/${cname}`);
  };

  const getExecutiveDetails = (id, name) => {
    if (!isMyDashboard) {
      showAlert(
        "info",
        "Redirection is not allowed when viewing another user's dashboard.",
      );
      return;
    }
    let cname = name.replaceAll(" ", "-", name);
    navigate(`/executive-details/${id}/${cname}`);
  };

  const columns = [
    {
      title: <div className="companyname">Company Name</div>,
      dataIndex: "name",
      render: (record, row) => {
        return (
          <div
            className="namecol"
            onClick={() => getDetails(row.key, record?.name)}
          >
            <div className="logo">
              <img
                src={getCompanyLogoUrl(record?.companyLogoUrl) || defaultLogo}
                alt={record?.name || "Company logo"}
                onError={(e) => {
                  e.currentTarget.src = defaultLogo;
                }}
              />
            </div>
            <span className="cname">{record?.name}</span>
          </div>
        );
      },
      fixed: "left",
    },
    {
      title: "Industry",
      dataIndex: "industry",
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      render: (text, row) => {
        return getPartialPhoneNumber(text);
      },
    },
  ];

  const executiveColumns = [
    {
      title: <div className="companyname">Executive Name</div>,
      dataIndex: "name",
      fixed: "left",
      render: (record, row, index) => {
        let cnt = index;
        return (
          <div
            className="namecol"
            onClick={() => getExecutiveDetails(row.key, record?.fullname)}
          >
            <span className="cname">
              {/* {record?.fullname} */}
              {record?.fullname
                ? record.fullname
                : record.firstname + " " + record.lastname}
            </span>
          </div>
        );
      },
    },
    {
      title: "Designation",
      dataIndex: "designation",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      className: "phone",
      render: (text, row) => {
        return getPartialPhoneNumber(text);
      },
    },
  ];

  return (
    <Row gutter={[20, 20]} className="tw-p-5 tw-bg-[#f8fcff] !tw-mx-0">
      {isCompanyView && (
        <Col xs={24} lg={12}>
          <div className="tw-p-6 tw-border tw-border-solid tw-border-border-secondary tw-rounded-[10px] tw-bg-white tw-h-full">
            <div className="tw-flex tw-items-center tw-gap-3 tw-text-base tw-font-semibold tw-text-text-primary tw-pb-4 tw-mb-4 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary">
              Company Recommendation
            </div>

            <div>
              <Table
                className="tw-custom-table tw-recommendation"
                loading={isLoading}
                pagination={false}
                dataSource={companyList}
                columns={columns}
              />
              {companyList?.length > 10 && (
                <CustomPagination total={companyList?.length} pageSize={10} />
              )}
            </div>
          </div>
        </Col>
      )}
      {isExecutiveView && (
        <Col xs={24} lg={12}>
          <div className="tw-p-6 tw-border tw-border-solid tw-border-border-secondary tw-rounded-[10px] tw-bg-white tw-h-full">
            <div className="tw-flex tw-items-center tw-gap-3 tw-text-base tw-font-semibold tw-text-text-primary tw-pb-4 tw-mb-4 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary">
              Executive Recommendation
            </div>

            <div>
              <Table
                className="tw-custom-table tw-recommendation"
                loading={isLoading}
                pagination={false}
                dataSource={employeeList}
                columns={executiveColumns}
              />
              {employeeList?.length > 10 && (
                <CustomPagination total={employeeList?.length} pageSize={10} />
              )}
            </div>
          </div>
        </Col>
      )}
    </Row>
  );
};

export default RecommendedReport;
